import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ImageIcon, XIcon } from "lucide-react";
import type { DocImage } from "../../data/caseStudies";
type DocLightboxProps = {
  doc: DocImage | null;
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function DocLightbox({
  doc,
  onClose
}: DocLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!doc) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      // Keep focus inside the dialog: cycle between the close button and the scroll area
      if (e.key === "Tab") {
        const items = [closeRef.current, scrollRef.current].filter(Boolean) as HTMLElement[];
        const i = items.indexOf(document.activeElement as HTMLElement);
        e.preventDefault();
        const nextIndex = e.shiftKey ? i <= 0 ? items.length - 1 : i - 1 : (i + 1) % items.length;
        items[nextIndex]?.focus();
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-39" data-mp="322ded7ev4pn9">
      {doc && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-4 md:p-10" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-40" data-mp="2y2czjlnuqcfo">
          <motion.div role="dialog" aria-modal="true" aria-label={`${doc.label}: ${doc.caption}`} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        duration: 0.25,
        ease
      }} onClick={e => e.stopPropagation()} className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-cream" data-id="element-41" data-mp="1ho38ukw50blo">
            <div className="flex items-center justify-between gap-4 px-6 py-4" data-id="element-42" data-mp="10dh8e29npqrn">
              <div className="min-w-0" data-id="element-43" data-mp="157brb3hfu1po">
                <p className="text-xs font-medium uppercase tracking-wide text-rust" data-id="element-44" data-mp="qhe68wet1s4p">{doc.label}</p>
                <p className="truncate text-espresso" data-id="element-45" data-mp="vfxu52cyldzp">{doc.caption}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-espresso text-cream transition-colors duration-150 hover:bg-rust" data-id="element-46" data-mp="hgogoag50xzf">
                <XIcon className="h-4 w-4" data-id="element-47" data-mp="3u12ap6p7536f" />
              </button>
            </div>
            <div ref={scrollRef} tabIndex={0} aria-label="Full-length image, scrollable" className="overflow-y-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rust" data-id="element-48" data-mp="buxfca9n7y3p">
              {doc.image ? <img src={doc.image} alt={doc.caption} decoding="async" className="w-full" data-id="element-49" data-mp="gua97eyxm5fs" /> : <div className="flex aspect-[9/20] w-full flex-col items-center justify-start gap-2 bg-paper pt-24 text-espresso/70" data-id="element-50" data-mp="vhdalwqdky8e">
                  <ImageIcon className="h-8 w-8" strokeWidth={1.25} data-id="element-51" data-mp="qleokfsirsb5" />
                  <span className="text-xs font-medium uppercase tracking-wide" data-id="element-52" data-mp="14juw28y7hmuc">
                    Full-length {doc.label.toLowerCase()} image
                  </span>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}